import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import {
  supportsFileSystemAccess,
  getParentFolder,
  getReceiptsFolder,
  writeBlobToFolder,
  getStoredFolderName,
} from '../utils/pdfSave';

// ── Design tokens (match the rest of the Finance area) ────────────────────
const NAVY    = '#1E3A4A';
const BORDER  = '#E5E7EB';
const MUTED   = '#64748B';
const TEXT    = '#0F172A';
const ACCENT  = '#F5C518';

const PAYMENT_METHODS = [
  { v: 'card',           l: 'Card' },
  { v: 'bank_transfer',  l: 'Bank Transfer' },
  { v: 'cash',           l: 'Cash' },
  { v: 'direct_debit',   l: 'Direct Debit' },
];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function fmtMoney(n) { return '$' + (Number(n) || 0).toFixed(2); }
function fmtMoney0(n) { return '$' + Math.round(Number(n) || 0).toLocaleString('en-AU'); }
function fmtDateDMY(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// FY ending year for any date (Australian: Jul-Jun)
function fyEnd(iso) {
  if (!iso) return null;
  const [y, m] = iso.split('-').map(Number);
  return m >= 7 ? y + 1 : y;
}
function currentFyEnd() { return fyEnd(todayIso()); }

// Sanitise vendor / description into a filename-safe slug
function slugify(s) {
  return String(s || '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function buildReceiptFilename({ date, vendor, total }, originalExt) {
  const d   = fmtDateDMY(date).replace(/\//g, '-');
  const v   = slugify(vendor) || 'NOVENDOR';
  const tot = '$' + Math.round(Number(total) || 0);
  const ext = (originalExt || 'pdf').replace(/^\./, '').toLowerCase();
  return `${d}_${v}_${tot}.${ext}`;
}

// Quick-stats: this month, this FY YTD, last FY total
function getQuickStats(rows) {
  const t   = todayIso();
  const ym  = t.slice(0, 7);
  const cFy = currentFyEnd();
  const lFy = cFy - 1;
  let monthSum = 0, fySum = 0, lastFySum = 0, count = 0;
  for (const r of rows) {
    const inc = Number(r.total_inc_gst) || 0;
    if (r.date && r.date.slice(0, 7) === ym) monthSum += inc;
    const fy = fyEnd(r.date);
    if (fy === cFy) fySum += inc;
    if (fy === lFy) lastFySum += inc;
    count++;
  }
  return { monthSum, fySum, lastFySum, count };
}

// ── Main component ─────────────────────────────────────────────────────────
export default function ExpensesPanel() {
  const [categories, setCategories] = useState([]);
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPayment,  setFilterPayment]  = useState('');
  const [filterVendor,   setFilterVendor]   = useState('');
  const [filterFrom,     setFilterFrom]     = useState('');
  const [filterTo,       setFilterTo]       = useState('');
  const [filterFy,       setFilterFy]       = useState(currentFyEnd());

  // Edit state
  const [editing, setEditing] = useState(null); // null | 'new' | row object
  const [folderName, setFolderName] = useState(null);

  // Zoho sync state (Finance Phase 2)
  const [zohoStatus, setZohoStatus] = useState(null);
  const [syncing, setSyncing]       = useState(false);

  const loadZohoStatus = useCallback(() => {
    api.expenseZohoStatus().then(setZohoStatus).catch(() => setZohoStatus(null));
  }, []);

  useEffect(() => {
    api.getExpenseCategories().then(setCategories).catch(console.error);
    getStoredFolderName().then(setFolderName).catch(() => setFolderName(null));
    loadZohoStatus();
  }, [loadZohoStatus]);

  async function handleZohoSync() {
    setSyncing(true);
    try {
      const r = await api.expenseZohoSync();
      if (!r.ok) alert(`Sync didn't run: ${r.reason || 'unknown'}`);
      load(); loadZohoStatus();
    } catch (e) {
      alert(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterFy)        params.fy = filterFy;
      if (filterCategory)  params.category = filterCategory;
      if (filterPayment)   params.payment_method = filterPayment;
      if (filterVendor)    params.vendor = filterVendor;
      if (filterFrom && filterTo) {
        params.from = filterFrom;
        params.to   = filterTo;
        delete params.fy;
      }
      const data = await api.getExpenses(params);
      setRows(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterFy, filterCategory, filterPayment, filterVendor, filterFrom, filterTo]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => getQuickStats(rows), [rows]);

  // Distinct vendors used in the loaded rows — autocomplete source
  const vendorSuggestions = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.vendor) set.add(r.vendor); });
    return [...set].sort();
  }, [rows]);

  function handleNew() {
    setEditing({
      _new: true,
      date: todayIso(),
      category: categories[0] || 'Materials & Supplies',
      description: '',
      vendor: '',
      amount_ex_gst: '',
      gst: '',
      total_inc_gst: '',
      has_gst: true,
      payment_method: 'card',
      receipt_filename: '',
      receipt_folder:   '',
      notes: '',
      _receiptFile: null, // local File object pending save
    });
  }

  return (
    <div>
      {/* ── Quick stats strip ────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12, marginBottom: 14,
      }}>
        <StatCard label="This month" value={fmtMoney0(stats.monthSum)} />
        <StatCard label={`FY ${filterFy - 1}–${String(filterFy).slice(2)}`} value={fmtMoney0(stats.fySum)} />
        <StatCard label={`FY ${filterFy - 2}–${String(filterFy - 1).slice(2)}`} value={fmtMoney0(stats.lastFySum)} sub="prior FY" />
        <StatCard label="Receipts" value={String(stats.count)} sub={stats.count === 1 ? 'expense' : 'expenses'} />
      </div>

      {/* ── Zoho Books sync bar (only when the server has Zoho configured) ── */}
      {zohoStatus && zohoStatus.configured && (
        <ZohoSyncBar status={zohoStatus} syncing={syncing} onSync={handleZohoSync} onChanged={loadZohoStatus} />
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12,
        padding: 12, marginBottom: 14, display: 'flex', flexWrap: 'wrap',
        gap: 10, alignItems: 'center',
      }}>
        <select
          value={filterFy}
          onChange={e => { setFilterFy(parseInt(e.target.value)); setFilterFrom(''); setFilterTo(''); }}
          style={selectStyle}
        >
          {[currentFyEnd(), currentFyEnd() - 1, currentFyEnd() - 2].map(y =>
            <option key={y} value={y}>FY {y - 1}–{String(y).slice(2)}</option>
          )}
        </select>

        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={selectStyle}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)} style={selectStyle}>
          <option value="">All methods</option>
          {PAYMENT_METHODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
        </select>

        <input
          type="text" placeholder="Search vendor…"
          value={filterVendor} onChange={e => setFilterVendor(e.target.value)}
          style={{ ...inputStyle, minWidth: 160 }}
        />

        <span style={{ fontSize: 11, color: MUTED }}>Or:</span>
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={inputStyle} />
        <span style={{ fontSize: 11, color: MUTED }}>→</span>
        <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} style={inputStyle} />

        {(filterCategory || filterPayment || filterVendor || filterFrom || filterTo) && (
          <button onClick={() => { setFilterCategory(''); setFilterPayment(''); setFilterVendor(''); setFilterFrom(''); setFilterTo(''); }}
            style={{ ...btnGhost, color: '#dc2626' }}>
            ✕ Clear filters
          </button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {supportsFileSystemAccess() && (
            <span style={{ fontSize: 11, color: MUTED }}>
              📁 Receipts → {folderName || '(pick on first save)'} / RECEIPTS /
            </span>
          )}
          <button onClick={handleNew} style={btnPrimary}>+ Add Expense</button>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>
            No expenses for these filters.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: `1px solid ${BORDER}` }}>
                <Th>Date</Th>
                <Th>Category</Th>
                <Th>Description</Th>
                <Th>Vendor</Th>
                <Th align="right">Ex GST</Th>
                <Th align="right">GST</Th>
                <Th align="right">Total</Th>
                <Th align="center">Receipt</Th>
                <Th>Method</Th>
                {zohoStatus && zohoStatus.configured && <Th align="center">Zoho</Th>}
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <Td>{fmtDateDMY(r.date)}</Td>
                  <Td>
                    {/* Zoho-sourced rows show Zoho's own account name — the app
                        mirrors Zoho rather than flattening into a fallback. */}
                    <CategoryPill name={r.source === 'zoho' && r.zoho_account_name ? r.zoho_account_name : r.category} />
                  </Td>
                  <Td>
                    <div style={{ fontWeight: 600, color: TEXT }}>{r.description}</div>
                    {r.notes && r.source !== 'zoho' && <div style={{ fontSize: 11, color: MUTED }}>{r.notes}</div>}
                  </Td>
                  <Td>{r.vendor || <span style={{ color: '#9ca3af' }}>—</span>}</Td>
                  <Td align="right">{fmtMoney(r.amount_ex_gst)}</Td>
                  <Td align="right" style={{ color: MUTED }}>{fmtMoney(r.gst)}</Td>
                  <Td align="right" style={{ fontWeight: 800, color: '#16a34a' }}>{fmtMoney(r.total_inc_gst)}</Td>
                  <Td align="center">
                    {r.receipt_filename
                      ? <span title={`${r.receipt_folder || ''} / ${r.receipt_filename}`} style={{ fontSize: 16 }}>📎</span>
                      : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </Td>
                  <Td style={{ fontSize: 11, color: MUTED }}>
                    {(PAYMENT_METHODS.find(p => p.v === r.payment_method) || { l: '—' }).l}
                  </Td>
                  {zohoStatus && zohoStatus.configured && (
                    <Td align="center">
                      {r.source === 'zoho' ? (
                        <span title="Entered in Zoho Books — imported by the sync" style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '2px 7px', borderRadius: 999 }}>Zoho</span>
                      ) : r.zoho_expense_id ? (
                        <span title={`Synced to Zoho Books${r.zoho_synced_at ? ' · ' + fmtDateDMY(r.zoho_synced_at) : ''}`} style={{ fontSize: 12, color: '#16a34a', fontWeight: 800 }}>✓</span>
                      ) : (
                        <span title="Not in Zoho yet — the nightly sync (or Sync now) will push it" style={{ fontSize: 12, color: '#d97706', fontWeight: 800 }}>…</span>
                      )}
                    </Td>
                  )}
                  <Td align="right" style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditing({ ...r, has_gst: Number(r.gst) > 0 })} style={btnGhost}>Edit</button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this expense?')) return;
                        try { await api.deleteExpense(r.id); load(); }
                        catch (e) { alert(e.message); }
                      }}
                      style={{ ...btnGhost, color: '#dc2626', marginLeft: 4 }}
                    >Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      {editing && (
        <ExpenseModal
          row={editing}
          categories={categories}
          vendorSuggestions={vendorSuggestions}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Zoho sync bar — status pill, Sync now, collapsible category mapping ────
function ZohoSyncBar({ status, syncing, onSync, onChanged }) {
  const [showMapping, setShowMapping] = useState(false);
  const [accounts, setAccounts]       = useState(null); // Zoho expense accounts
  const [savingCat, setSavingCat]     = useState(null);

  const counts   = status.counts || {};
  const unmapped = (status.mapping || []).filter(m => !m.zoho_account_id).length;
  const last     = status.last_sync;

  async function toggleMapping() {
    const next = !showMapping;
    setShowMapping(next);
    if (next && !accounts) {
      try { setAccounts(await api.expenseZohoAccounts()); }
      catch (e) { setAccounts([]); alert(`Couldn't load Zoho accounts: ${e.message}`); }
    }
  }

  async function changeMapping(category, accountId) {
    const acc = (accounts || []).find(a => a.id === accountId) || null;
    setSavingCat(category);
    try {
      await api.expenseZohoMapping({ category, zoho_account_id: acc ? acc.id : null, zoho_account_name: acc ? acc.name : null });
      onChanged();
    } catch (e) { alert(e.message); }
    finally { setSavingCat(null); }
  }

  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '10px 12px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: NAVY }}>🔗 Zoho Books sync</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#15803D', background: '#DCFCE7', padding: '2px 9px', borderRadius: 999 }}>
          {counts.pushed || 0} synced
        </span>
        {(counts.unsynced || 0) > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '2px 9px', borderRadius: 999 }}>
            {counts.unsynced} pending push
          </span>
        )}
        {(counts.pulled || 0) > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '2px 9px', borderRadius: 999 }}>
            {counts.pulled} from Zoho
          </span>
        )}
        {unmapped > 0 && (
          <span title="These categories can't push until they're mapped to a Zoho account"
            style={{ fontSize: 11, fontWeight: 700, color: '#991B1B', background: '#FEE2E2', padding: '2px 9px', borderRadius: 999 }}>
            {unmapped} categor{unmapped === 1 ? 'y' : 'ies'} unmapped
          </span>
        )}
        <span style={{ fontSize: 11, color: MUTED }}>
          {last
            ? `Last sync ${fmtDateDMY(last.at)} — pushed ${last.pushed}, imported ${last.imported}` +
              (last.updated ? `, updated ${last.updated}` : '') +
              (last.removed ? `, removed ${last.removed}` : '')
            : 'Runs nightly at 02:15'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={toggleMapping} style={btnGhost}>{showMapping ? 'Hide mapping' : 'Category mapping'}</button>
          <button onClick={onSync} disabled={syncing}
            style={{ ...btnPrimary, opacity: syncing ? 0.6 : 1, cursor: syncing ? 'wait' : 'pointer' }}>
            {syncing ? 'Syncing…' : '⟳ Sync now'}
          </button>
        </div>
      </div>

      {showMapping && (
        <div style={{ marginTop: 10, borderTop: `1px solid ${BORDER}`, paddingTop: 10 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 8 }}>
            Each app category posts to this Zoho Books expense account. Paid through:{' '}
            <strong>{status.paid_through && status.paid_through.name ? status.paid_through.name : 'auto-detected on first sync'}</strong>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 6 }}>
            {(status.mapping || []).map(m => (
              <div key={m.category} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: TEXT, flex: '0 0 175px' }}>{m.category}</span>
                <select
                  value={m.zoho_account_id || ''}
                  disabled={!accounts || savingCat === m.category}
                  onChange={e => changeMapping(m.category, e.target.value || null)}
                  style={{ ...selectStyle, flex: 1, borderColor: m.zoho_account_id ? BORDER : '#FCA5A5' }}
                >
                  <option value="">— not mapped —</option>
                  {(accounts || (m.zoho_account_id ? [{ id: m.zoho_account_id, name: m.zoho_account_name }] : [])).map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: '#FAFAF7', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, letterSpacing: -0.3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Th({ children, align = 'left' }) {
  return (
    <th style={{
      padding: '10px 12px', fontSize: 11, fontWeight: 700, color: MUTED,
      textAlign: align, textTransform: 'uppercase', letterSpacing: 0.5,
      whiteSpace: 'nowrap',
    }}>{children}</th>
  );
}
function Td({ children, align = 'left', style }) {
  return (
    <td style={{ padding: '10px 12px', textAlign: align, color: TEXT, ...style }}>{children}</td>
  );
}

const CATEGORY_COLORS = {
  'Materials & Supplies':     { bg: '#FEF3C7', fg: '#92400E' },
  'Vehicle Expenses':         { bg: '#DBEAFE', fg: '#1E40AF' },
  'Insurance':                { bg: '#E0E7FF', fg: '#3730A3' },
  'Software & Subscriptions': { bg: '#F3E8FF', fg: '#6B21A8' },
  'Bank & Merchant Fees':     { bg: '#FEE2E2', fg: '#991B1B' },
  'Phone & Internet':         { bg: '#CFFAFE', fg: '#155E75' },
  'Marketing & Advertising':  { bg: '#FCE7F3', fg: '#9D174D' },
  'Office Supplies':          { bg: '#D1FAE5', fg: '#065F46' },
  'Professional Fees':        { bg: '#E0E7FF', fg: '#3730A3' },
  'Hosting & Domain':         { bg: '#F0F9FF', fg: '#075985' },
  'Workers Comp':             { bg: '#FEF3C7', fg: '#854D0E' },
  'Other Operating Expenses': { bg: '#F3F4F6', fg: '#374151' },
};
function CategoryPill({ name }) {
  const c = CATEGORY_COLORS[name] || CATEGORY_COLORS['Other Operating Expenses'];
  return (
    <span style={{
      background: c.bg, color: c.fg, padding: '3px 9px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{name}</span>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────
function ExpenseModal({ row, categories, vendorSuggestions, onCancel, onSaved }) {
  const isNew = !!row._new;
  const [date, setDate]                   = useState(row.date || todayIso());
  const [category, setCategory]           = useState(row.category || categories[0] || '');
  const [description, setDescription]     = useState(row.description || '');
  const [vendor, setVendor]               = useState(row.vendor || '');
  const [amountEx, setAmountEx]           = useState(String(row.amount_ex_gst ?? ''));
  const [hasGst, setHasGst]               = useState(row.has_gst ?? Number(row.gst) > 0 ?? true);
  const [paymentMethod, setPaymentMethod] = useState(row.payment_method || 'card');
  const [notes, setNotes]                 = useState(row.notes || '');

  // Receipt: existing reference (filename + folder), or a freshly chosen local File
  const [receiptFile, setReceiptFile]         = useState(null);
  const [receiptFilename, setReceiptFilename] = useState(row.receipt_filename || '');
  const [receiptFolder,   setReceiptFolder]   = useState(row.receipt_folder || '');

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const exNum = parseFloat(amountEx) || 0;
  const gstNum = hasGst ? +(exNum * 0.1).toFixed(2) : 0;
  const totalNum = +(exNum + gstNum).toFixed(2);

  function handlePickReceipt(e) {
    const f = e.target.files?.[0];
    if (f) setReceiptFile(f);
    e.target.value = '';
  }

  async function handleSave() {
    setError('');
    if (!description.trim()) { setError('Description is required.'); return; }
    if (!exNum || exNum <= 0) { setError('Amount must be greater than zero.'); return; }

    setSaving(true);
    try {
      // Step 1: save the receipt file to OneDrive FIRST, while the live user
      // gesture is still valid (showDirectoryPicker / requestPermission both
      // need an active gesture). If File System Access isn't supported we
      // skip and just record nothing.
      let savedFilename = receiptFilename;
      let savedFolder   = receiptFolder;

      if (receiptFile && supportsFileSystemAccess()) {
        const parent = await getParentFolder(); // may prompt the picker
        if (!parent) throw new Error('No OneDrive parent folder selected.');
        const folder = await getReceiptsFolder(parent, category);
        // Build a deterministic filename: DATE_VENDOR_$AMOUNT.<ext>
        const ext = (receiptFile.name.match(/\.([^.]+)$/) || [, 'pdf'])[1];
        const fname = buildReceiptFilename({ date, vendor, total: totalNum }, ext);
        await writeBlobToFolder(folder, fname, receiptFile);
        savedFilename = fname;
        savedFolder   = `RECEIPTS / ${category}`;
      }

      const body = {
        date, category, description: description.trim(),
        vendor: vendor.trim() || null,
        amount_ex_gst: exNum,
        gst: gstNum,
        total_inc_gst: totalNum,
        payment_method: paymentMethod,
        receipt_filename: savedFilename || null,
        receipt_folder:   savedFolder   || null,
        notes: notes.trim() || null,
      };

      if (isNew) await api.createExpense(body);
      else        await api.updateExpense(row.id, body);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={modalBackdrop}>
      <div style={modalShell}>
        <div style={modalHeader}>
          <div style={{ fontSize: 16, fontWeight: 800, color: TEXT }}>
            {isNew ? 'Add Expense' : 'Edit Expense'}
          </div>
          <button onClick={onCancel} disabled={saving} style={iconClose}>✕</button>
        </div>

        <div style={modalBody}>
          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <Row2>
            <Field label="Date">
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Category">
              <select value={category} onChange={e => setCategory(e.target.value)} style={selectStyle}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </Row2>

          <Field label="Description *">
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Polish, microfibre cloths, Telstra mobile" style={inputStyle} />
          </Field>

          <Field label="Vendor">
            <input type="text" list="vendor-suggestions" value={vendor}
              onChange={e => setVendor(e.target.value)}
              placeholder="e.g. Bunnings, Telstra" style={inputStyle} />
            <datalist id="vendor-suggestions">
              {vendorSuggestions.map(v => <option key={v} value={v} />)}
            </datalist>
          </Field>

          <Row2>
            <Field label="Amount (ex GST) *">
              <input type="number" step="0.01" min="0" value={amountEx}
                onChange={e => setAmountEx(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="GST">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: TEXT, padding: '8px 0' }}>
                <input type="checkbox" checked={hasGst} onChange={e => setHasGst(e.target.checked)} />
                GST applicable (auto 10%)
              </label>
            </Field>
          </Row2>

          <div style={{
            background: '#F8FAFC', border: `1px solid ${BORDER}`, borderRadius: 8, padding: 10,
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14,
          }}>
            <Calc label="Ex GST" value={fmtMoney(exNum)} />
            <Calc label="+ GST" value={fmtMoney(gstNum)} muted />
            <Calc label="Total inc GST" value={fmtMoney(totalNum)} bold />
          </div>

          <Row2>
            <Field label="Payment method">
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={selectStyle}>
                {PAYMENT_METHODS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </Field>
            <Field label="Receipt (PDF/JPG/PNG)">
              <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={handlePickReceipt}
                style={{ ...inputStyle, padding: '6px 8px' }} />
              {receiptFile && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>
                  ✓ {receiptFile.name} — will save to RECEIPTS / {category} on submit
                </div>
              )}
              {!receiptFile && receiptFilename && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                  📎 {receiptFilename} ({receiptFolder})
                </div>
              )}
            </Field>
          </Row2>

          <Field label="Notes">
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional reference, OP number, etc." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </Field>
        </div>

        <div style={modalFooter}>
          <button onClick={onCancel} disabled={saving} style={btnGhost}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : isNew ? 'Save expense' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
function Row2({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  );
}
function Calc({ label, value, muted, bold }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: bold ? 16 : 14, fontWeight: bold ? 800 : 600, color: muted ? MUTED : TEXT }}>{value}</div>
    </div>
  );
}

// ── Style tokens ───────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${BORDER}`, borderRadius: 8, background: '#fff',
  color: TEXT, boxSizing: 'border-box', outline: 'none',
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };

const btnPrimary = {
  padding: '8px 18px', borderRadius: 8, border: 'none',
  background: NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px', borderRadius: 6, border: `1px solid ${BORDER}`,
  background: '#fff', color: TEXT, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalShell = {
  background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640,
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const modalHeader = {
  padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
};
const modalBody = { padding: '16px 20px', overflowY: 'auto', flex: 1 };
const modalFooter = {
  padding: '14px 20px', borderTop: `1px solid ${BORDER}`,
  display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0,
};
const iconClose = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: MUTED, fontSize: 18, padding: 4,
};
